// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Button } from "@insite/mobius/index";
import { TextFieldProps } from "@insite/mobius/TextField";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import SignInButton, { SignInButtonStyles } from "@werner/App/Components/Common/SignInButton";
import MoreInfoButton from "@werner/App/Components/MoreInfoButton/MoreInfoButton";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { getIsLeadCapture } from "@werner/App/Store/Selectors/ProductSelectors";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import ProductAddToCartButton from "@werner/Overrides/Components/ProductAddToCartButton";
import { mergeWithCss } from "@werner/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductActionsProps {
    carouselId?: string;
    iconOnly?: boolean;
    showActions?: boolean;
    alwaysShowQty?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
}

type Props = ProductActionsProps & HasProductContext;

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    signInButton?: SignInButtonStyles;
    productUomWrapper?: GridItemProps;
    viewOptionsButton?: ButtonPresentationProps;
}

const productActionsStyle: ProductActionsStyle = {
    addToCartContainer: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            flex-wrap: wrap;
            row-gap: 8px;
            column-gap: 8px;
            .has-uom {
                flex-direction: column;
            }
        `,
    },
    qtyWrapper: {
        css: css`
            max-width: 100px;
        `,
    },

    productAddToCartButtonStyles: {
        mergeCss: true,
        sizeVariant: "medium",
        css: css`
            &:not(.button-wrapper-icon-only) {
                width: 100%;
                min-width: unset;
            }
        `,
    },
    productQuantityOrderedStyles: {
        sizeVariant: "small",
        cssOverrides: {
            inputSelect: css`
                text-align: center;
                padding: 0 10px !important;
            `,
        },
    },
    addToCartButtonWrapper: {
        css: css`
            flex-grow: 1;
        `,
    },
    productUomWrapper: {
        css: css`
            flex-grow: 1;
            & > div {
                width: 100%;
                max-width: unset;
            }
        `,
    },
    viewOptionsButton: {
        css: css`
            width: 100%;
        `,
    },
};

const ProductActions: FC<Props> = ({ carouselId, extendedStyles, onAddToCartClick, iconOnly, productContext }) => {
    const history = useHistory();
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    const {
        isAuthenticated,
        isLeadCapture,
        configurationCompleted,
        variantSelectionCompleted,
        configurationSelection,
        subscription,
        productSettings,
        isProductDetailsPage,
        isCurrentSelectedProductInPDP,
    } = useAppSelector(state => {
        const currentPage = getCurrentPage(state)?.type;
        const isProductDetailsPage = currentPage === "ProductDetailsPage";
        const currentSelectedProduct = state.pages.productDetails.selectedProductId;
        const isCurrentSelectedProductInPDP = currentSelectedProduct === productContext.product.id;
        return {
            isAuthenticated: xIsAuthenticated(state),
            isLeadCapture: getIsLeadCapture(productContext.product),
            configurationCompleted: isProductDetailsPage && state.pages.productDetails.configurationCompleted,
            variantSelectionCompleted: isProductDetailsPage && state.pages.productDetails.variantSelectionCompleted,
            configurationSelection: isProductDetailsPage
                ? state.pages.productDetails.configurationSelection
                : undefined,
            subscription: isProductDetailsPage ? state.pages.productDetails.subscription : undefined,
            productSettings: getSettingsCollection(state).productSettings,
            isProductDetailsPage,
            isCurrentSelectedProductInPDP,
        };
    });

    const handleViewOptionsClick = () => {
        history.push(productContext.productInfo.productDetailPath);
    };

    if (productContext.product.isVariantParent && isProductDetailsPage && isCurrentSelectedProductInPDP) {
        return null;
    }

    if (productContext.product.isVariantParent) {
        return (
            <Button onClick={handleViewOptionsClick} {...styles.viewOptionsButton}>
                {translate("View Options")}
            </Button>
        );
    }

    if (!isAuthenticated) {
        return <SignInButton extendedStyles={styles.signInButton} labelOverride={translate("Sign In For Price")} />;
    }

    if (isLeadCapture) {
        return <MoreInfoButton />;
    }

    if (productContext.product.isDiscontinued) {
        return null;
    }
    const inputId = `${uniqueId()}-qty-label`;
    const unitOfMeasures = productContext.product.unitOfMeasures;
    const willShowUnitOfMeasureSelect = !(
        !productSettings?.alternateUnitsOfMeasure ||
        !unitOfMeasures ||
        unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
    );

    if (willShowUnitOfMeasureSelect) {
        return (
            <StyledWrapper {...styles.addToCartContainer} className="has-uom" data-test-selector="product_actions">
                <StyledWrapper {...styles.addToCartContainer}>
                    <StyledWrapper {...styles.qtyWrapper}>
                        <VisuallyHidden as="label" htmlFor={inputId}>
                            {translate("QTY")}
                        </VisuallyHidden>
                        <ProductQuantityOrdered
                            labelOverride=""
                            extendedStyles={styles.productQuantityOrderedStyles}
                            data-test-selector="productQty"
                            id={inputId}
                            configurationCompleted={configurationCompleted}
                            variantSelectionCompleted={variantSelectionCompleted}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productUomWrapper}>
                        {createWidgetElement("ProductDetails/ProductDetailsUnitOfMeasureSelect", {
                            fields: {},
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.addToCartButtonWrapper}>
                    <ProductAddToCartButton
                        {...{ iconOnly }}
                        onAddToCartClick={onAddToCartClick}
                        extendedStyles={styles.productAddToCartButtonStyles}
                        data-test-selector="addToCartBtn"
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        subscription={subscription}
                    />
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            <StyledWrapper {...styles.qtyWrapper}>
                <VisuallyHidden as="label" htmlFor={inputId}>
                    {translate("QTY")}
                </VisuallyHidden>
                <ProductQuantityOrdered
                    labelOverride=""
                    extendedStyles={styles.productQuantityOrderedStyles}
                    data-test-selector="productQty"
                    id={inputId}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                />
            </StyledWrapper>

            <StyledWrapper {...styles.addToCartButtonWrapper}>
                <ProductAddToCartButton
                    {...{ iconOnly }}
                    onAddToCartClick={onAddToCartClick}
                    extendedStyles={styles.productAddToCartButtonStyles}
                    data-test-selector="addToCartBtn"
                    configurationSelection={configurationSelection}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    subscription={subscription}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductActions);
